import React from 'react';
import {useTranslation} from 'react-i18next';
import {UNIT_CURRENCY} from '../rulesModel';

// «La línea no mide esta métrica»: el nombre de siempre y el de v2.
const NOT_APPLICABLE_REASONS = ['METRIC_NOT_AVAILABLE', 'NOT_MEASURABLE'];
// Los motivos de v2 (contrato v2 §5): una métrica de la línea sin presupuesto o sin objetivo, o una
// comparación sin período anterior. Cada uno se cuenta aparte: «sin datos» no los explica.
const REASON_NOTES = {
    NO_BUDGET: 'alerts_center.builder.simulate_no_budget',
    NO_GOAL: 'alerts_center.builder.simulate_no_goal',
    NO_PREVIOUS_PERIOD: 'alerts_center.builder.simulate_no_previous_period',
    // Una comparación sin valor contra qué comparar (review C-av2-fe-centro-12): no es «sin datos».
    NO_COMPARISON: 'alerts_center.builder.simulate_no_comparison',
};

// El ritmo viaja con signo (negativo = va lento) y la condición compara el absoluto (contrato v2 §5).
const PACING_METRIC = 'LINE:PACING_DEVIATION_PCT';

function formatNumber(value, language) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    try {
        return new Intl.NumberFormat(language || 'es', {maximumFractionDigits: 2}).format(number);
    } catch (e) {
        return String(number);
    }
}

/**
 * El valor de una línea que dispararía. En una comparación (`PERIOD_CHANGE`) el umbral es un porcentaje de cambio:
 * se dice el cambio y los dos valores comparados («−50 % (de 4 a 2), umbral 25 %»); el valor solo contra un
 * umbral en % mezclaba unidades (review C-av2-fe-centro-12).
 */
function hitText(hit, t, language, metric) {
    // «−30 %» solo no dice qué es: se dice si va lento o rápido, y cuánto.
    if (metric === PACING_METRIC && typeof hit.value === 'number' && typeof hit.change_pct !== 'number') {
        return t(hit.value < 0 ? 'alerts_center.builder.simulate_pacing_slow' : 'alerts_center.builder.simulate_pacing_fast', {
            value: `${formatNumber(Math.abs(hit.value), language)} %`, threshold: hit.threshold_formatted,
            interpolation: {escapeValue: false},
        });
    }
    if (typeof hit.change_pct === 'number') {
        const sign = hit.change_pct > 0 ? '+' : (hit.change_pct < 0 ? '−' : '');
        return t('alerts_center.builder.simulate_change', {
            change: `${sign}${formatNumber(Math.abs(hit.change_pct), language)} %`,
            from: formatNumber(hit.value_vs, language),
            to: hit.value_formatted !== undefined ? hit.value_formatted : formatNumber(hit.value, language),
            threshold: hit.threshold_formatted,
            interpolation: {escapeValue: false},
        });
    }
    return t('alerts_center.builder.simulate_value', {
        value: hit.value_formatted, threshold: hit.threshold_formatted, interpolation: {escapeValue: false},
    });
}

// `data_as_of` es el sync MÁS VIEJO de las líneas evaluadas (a propósito, del backend): con el año si no es el de hoy, y
// dicho como atraso si tiene más de unos días. «8 de abril» de un dato de 2025 se leía como de este abril (QA-av2c-01).
const STALE_DATA_DAYS = 3;

function formatWhen(date, language) {
    const options = {day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'};
    if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    return date.toLocaleString(language || undefined, options);
}

function asOfNote(iso, t, language) {
    if (!iso) return null;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return null;
    const stale = Date.now() - date.getTime() > STALE_DATA_DAYS * 24 * 60 * 60 * 1000;
    return t(stale ? 'alerts_center.builder.simulate_as_of_stale' : 'alerts_center.builder.simulate_as_of', {
        when: formatWhen(date, language), interpolation: {escapeValue: false},
    });
}

/**
 * Lo que devuelve «Simular»: en qué líneas dispararía HOY la regla, con los últimos valores
 * guardados (no le pega a las plataformas), cuáles no se pueden evaluar y de cuándo son los datos.
 * Es el freno a una regla ruidosa antes de guardarla.
 *
 * El titular cuenta sobre las líneas que SE PUDIERON evaluar (C-ac-fe-centro-02): «no dispararía en
 * ninguna de las 12» con las 12 sin datos afirmaba lo que no se miró. Sin ninguna evaluable lo dice
 * así, y las no evaluables se separan por motivo: sin datos todavía, una métrica que la línea no
 * mide, o (v2) una línea sin presupuesto, sin objetivo o sin período anterior. La moneda va sólo con
 * una métrica de moneda (C-ac-fe-centro-06).
 *
 * El editor del número de una fila (v2) lo usa con `headlineValue` («Con 115 %, hoy avisaría en 2 de 24
 * líneas: …»), `maxHits` (las primeras y «y N más») y `compact` (sin la nota de «no avisa en el acto»,
 * que el editor dice a su manera).
 *
 * D8 (fede, 18/10): cambiar el número de una alerta no re-arma; avisa sólo en las líneas que el cambio pone
 * en rojo. Con `rule_id` el backend manda `would_notify_count` y `would_notify` por línea, y el titular separa
 * las dos cosas: «Con 105 %, hoy estarían en rojo 5 de 24 líneas. Te avisaría de 2 nuevas: …» (o «Ya
 * estaban en rojo: no te avisaría de nuevo»). Las que avisarían van primero y marcadas.
 * En el editor, sin el campo (un backend sin D8), «estarían en rojo» igual: con D8 cambiar el número no re-arma, y
 * «avisaría en N» contradecía al toast de guardado (review C-av2-fe-centro-14). En el constructor (regla nueva,
 * sin `headlineValue`), el texto de siempre.
 *
 * `metric`: la de la condición. Con la del ritmo (`LINE:PACING_DEVIATION_PCT`), cada línea dice si va lenta o rápida.
 */
export function SimulationResult({simulation, unit = null, headlineValue = null, maxHits = null, compact = false,
                                     metric = null}) {
    const {t, i18n} = useTranslation();
    const notifyCount = headlineValue !== null && typeof simulation.would_notify_count === 'number'
        ? simulation.would_notify_count : null;
    const rawTriggers = simulation.would_trigger || [];
    // Las que avisarían, primero: son las que nombra «Te avisaría de N nuevas: …».
    const triggers = notifyCount === null ? rawTriggers
        : [...rawTriggers.filter((hit) => hit.would_notify === true), ...rawTriggers.filter((hit) => hit.would_notify !== true)];
    const notEvaluable = simulation.not_evaluable || [];
    const total = typeof simulation.evaluated_lines === 'number'
        ? simulation.evaluated_lines : triggers.length + notEvaluable.length;
    const evaluable = typeof simulation.evaluable_lines === 'number'
        ? simulation.evaluable_lines : Math.max(0, total - notEvaluable.length);
    const notApplicable = notEvaluable.filter((line) => NOT_APPLICABLE_REASONS.includes(line.reason)).length;
    const byReason = Object.keys(REASON_NOTES)
        .map((reason) => ({reason, count: notEvaluable.filter((line) => line.reason === reason).length}))
        .filter((entry) => entry.count > 0);
    const explained = byReason.reduce((sum, entry) => sum + entry.count, 0);
    const noData = notEvaluable.length - notApplicable - explained;
    const asOf = asOfNote(simulation.data_as_of, t, i18n.language);
    const showCurrency = unit === UNIT_CURRENCY;

    let headline;
    if (headlineValue !== null) {
        const params = {value: headlineValue, interpolation: {escapeValue: false}};
        if (evaluable === 0) headline = t('alerts_center.editor.sim_none_evaluable', {...params, total});
        else if (triggers.length === 0) headline = t('alerts_center.editor.sim_red_none', {...params, total: evaluable});
        else {
            // Dos números no se pluralizan en una sola clave: «en rojo» y «te avisaría» van en dos.
            const red = t('alerts_center.editor.sim_red', {...params, count: triggers.length, total: evaluable});
            if (notifyCount === null) headline = red;
            else {
                const notify = notifyCount > 0
                    ? t('alerts_center.editor.sim_notify', {count: notifyCount})
                    : t('alerts_center.editor.sim_notify_none');
                headline = `${red} ${notify}`;
            }
        }
    } else if (evaluable === 0) headline = t('alerts_center.builder.simulate_none_evaluable', {total});
    else if (triggers.length === 0) headline = t('alerts_center.builder.simulate_result_none', {total: evaluable});
    else headline = t('alerts_center.builder.simulate_result', {count: triggers.length, total: evaluable});

    const shown = maxHits ? triggers.slice(0, maxHits) : triggers;
    const hidden = triggers.length - shown.length;
    const tone = evaluable === 0 ? ' -unknown' : (triggers.length ? ' -hits' : ' -clear');
    return (
        <section className={`alc-sim${tone}${compact ? ' -compact' : ''}`} aria-live="polite" data-testid="alc-simulation">
            <p className="alc-sim__headline">{headline}</p>
            {shown.length > 0 && (
                <ul className="alc-sim__list">
                    {shown.map((hit) => (
                        <li key={hit.business_unit_id} className={`alc-sim__hit${notifyCount !== null && hit.would_notify === true ? ' -notify' : ''}`}
                            data-would-notify={notifyCount !== null ? String(hit.would_notify === true) : undefined}>
                            <span className="alc-sim__where">
                                {hit.client_name ? `${hit.client_name} · ` : ''}{hit.business_unit_name}
                                {notifyCount !== null && hit.would_notify === true && (
                                    <span className="alc-sim__tag">{t('alerts_center.editor.sim_would_notify')}</span>
                                )}
                            </span>
                            <span className="alc-sim__value">
                                {hitText(hit, t, i18n.language, metric)}
                                {showCurrency && hit.currency && typeof hit.change_pct !== 'number' ? ` ${hit.currency}` : ''}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {hidden > 0 && <p className="alc-sim__note">{t('alerts_center.editor.sim_more', {count: hidden})}</p>}
            {noData > 0 && (
                <p className="alc-sim__note">{t('alerts_center.builder.simulate_not_evaluable', {count: noData})}</p>
            )}
            {notApplicable > 0 && (
                <p className="alc-sim__note">{t('alerts_center.builder.simulate_not_applicable', {count: notApplicable})}</p>
            )}
            {byReason.map(({reason, count}) => (
                <p key={reason} className="alc-sim__note" data-reason={reason}>{t(REASON_NOTES[reason], {count})}</p>
            ))}
            {asOf && <p className="alc-sim__note" data-testid="alc-sim-as-of">{asOf}</p>}
            {!compact && <p className="alc-sim__note">{t('alerts_center.builder.no_trigger_now_hint')}</p>}
        </section>
    );
}
